/**
 * /payroll-l2/anomalies — AI-detected labor cost anomalies.
 * Shows Claude-generated z-score anomalies with explanation, severity badge,
 * and acknowledge action.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import PsychologyIcon from "@mui/icons-material/Psychology";
import CheckIcon from "@mui/icons-material/Check";

import { api } from "@/lib/api";
import type { AiLaborAnomaly, AnomalySeverity } from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l2/anomalies")({
  component: AnomaliesPage,
});

const SEVERITY_COLOR: Record<AnomalySeverity, "default" | "info" | "warning" | "error"> = {
  info: "info",
  warn: "warning",
  critical: "error",
};

const SEV_TABS: { value: AnomalySeverity | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "info", label: "Info" },
  { value: "warn", label: "Warning" },
  { value: "critical", label: "Critical" },
];

function AnomaliesPage() {
  const [tab, setTab] = useState<AnomalySeverity | "all">("all");
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["payroll-l2", "anomalies", tab],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (tab !== "all") params.set("severity", tab);
      return await api<AiLaborAnomaly[]>(`/api/payroll-l2/ai/anomalies?${params}`);
    },
  });

  const ackMut = useMutation({
    mutationFn: async (id: string) => {
      return await api(`/api/payroll-l2/ai/anomalies/${id}/acknowledge`, { method: "POST" });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payroll-l2", "anomalies"] }),
  });

  const criticalCount = data?.filter((a) => a.severity === "critical" && !a.acknowledged).length ?? 0;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" alignItems="center" gap={1}>
          <PsychologyIcon color="primary" />
          <Typography variant="h5" fontWeight={600}>AI Anomalies</Typography>
          {criticalCount > 0 && (
            <Chip label={`${criticalCount} critical`} color="error" size="small" />
          )}
        </Stack>
        <HelpHint docPath="payroll-l2/anomalies.md" label="Anomaly detection help" />
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {SEV_TABS.map((t) => <Tab key={t.value} value={t.value} label={t.label} />)}
      </Tabs>

      {isLoading && <CircularProgress size={24} />}
      {error && <Alert severity="error">Failed to load anomalies.</Alert>}

      {data && data.length === 0 && (
        <Alert severity="success">No anomalies detected for the current filters.</Alert>
      )}

      {data && data.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Metric</TableCell>
              <TableCell>Observed</TableCell>
              <TableCell>Expected</TableCell>
              <TableCell>Z-Score</TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Explanation</TableCell>
              <TableCell>Ack</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((a) => (
              <TableRow
                key={a.id}
                hover
                sx={{ opacity: a.acknowledged ? 0.5 : 1 }}
              >
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{a.metric}</TableCell>
                <TableCell>{a.observed_value ?? "—"}</TableCell>
                <TableCell>{a.expected_value ?? "—"}</TableCell>
                <TableCell>{a.z_score ? Number(a.z_score).toFixed(2) : "—"}</TableCell>
                <TableCell>
                  <Chip label={a.severity} color={SEVERITY_COLOR[a.severity]} size="small" />
                </TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  <Tooltip title={a.explanation ?? ""} placement="top">
                    <span className="line-clamp-2 text-sm">{a.explanation ?? "—"}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {!a.acknowledged ? (
                    <Button
                      size="small"
                      startIcon={<CheckIcon />}
                      onClick={() => ackMut.mutate(a.id)}
                      disabled={ackMut.isPending}
                    >
                      Ack
                    </Button>
                  ) : (
                    <Chip label="Acked" size="small" color="default" />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
